import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BlogPostType } from '../../types';
import { blogPosts, categories } from './posts';

const BlogListPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('All');

  const posts = useMemo<BlogPostType[]>(() => {
    const fromLocal = localStorage.getItem('blogPosts');
    const stored = fromLocal ? (JSON.parse(fromLocal) as BlogPostType[]) : [];
    return stored.length ? stored : blogPosts;
  }, []);

  const filtered = posts.filter(p => {
    const matchesCategory = category === 'All' || p.category === category;
    const q = query.toLowerCase();
    const matchesQuery = !q || [p.title, p.excerpt, p.tags.join(' ')].join(' ').toLowerCase().includes(q);
    return matchesCategory && matchesQuery;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-purple-200 to-blue-100 rounded-full blur-3xl"
          animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-20 -left-20 w-96 h-96 bg-gradient-to-br from-blue-200 to-cyan-100 rounded-full blur-3xl"
          animate={{ y: [0, -30, 0], x: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
      </div>

      {/* Hero */}
      <section className="pt-24 pb-12 px-4 relative z-10">
        <motion.div
          className="max-w-6xl mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1
            variants={itemVariants}
            className="text-4xl md:text-6xl font-extrabold mb-4 bg-gradient-to-r from-slate-900 via-purple-800 to-slate-900 bg-clip-text text-transparent"
          >
            Insights for Growth-Focused Brands
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto"
          >
            Practical playbooks on SEO, ads, content, and development — written by the performance team.
          </motion.p>
        </motion.div>
      </section>

      {/* Filters */}
      <section className="px-4 pb-6 relative z-10">
        <motion.div
          className="max-w-6xl mx-auto flex flex-col md:flex-row gap-4 items-center justify-between"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex flex-wrap gap-2">
            {categories.map((c, idx) => (
              <motion.button
                key={c}
                onClick={() => setCategory(c)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 rounded-full border font-medium transition-all ${
                  category === c
                    ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white border-primary-600 shadow-lg shadow-primary-500/50'
                    : 'bg-white/80 text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
              >
                {c}
              </motion.button>
            ))}
          </div>
          <motion.input
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search articles..."
            className="w-full md:w-80 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white/80 backdrop-blur transition-all"
          />
        </motion.div>
      </section>

      {/* Grid */}
      <section className="px-4 pb-20 relative z-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((post, idx) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.08, ease: 'easeOut' }}
              whileHover={{
                y: -6,
                scale: 1.02,
                boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
              }}
              className="bg-white/95 backdrop-blur rounded-xl border border-gray-100 overflow-hidden flex flex-col h-full hover:border-primary-300 transition-all duration-300"
            >
              {post.image && (
                <div className="relative h-48 overflow-hidden group">
                  <motion.img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500"
                    whileHover={{ scale: 1.08 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
                  <motion.div
                    className="absolute bottom-3 left-3 px-3 py-1 text-xs font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-full shadow-lg"
                    whileHover={{ scale: 1.05 }}
                  >
                    {post.category}
                  </motion.div>
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <motion.div
                  className="flex items-center gap-2 text-sm text-gray-500 mb-3"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <span className="inline-block w-2 h-2 rounded-full bg-primary-500" />
                  <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                </motion.div>
                <motion.h3
                  className="text-xl font-bold mb-2 leading-snug text-gray-900"
                  whileHover={{ color: '#7c3aed' }}
                >
                  {post.title}
                </motion.h3>
                <motion.p className="text-gray-600 mb-6 line-clamp-3 text-sm leading-relaxed">
                  {post.excerpt}
                </motion.p>
                <div className="mt-auto">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-2 text-primary-600 font-semibold group"
                  >
                    <motion.span whileHover={{ x: -3 }}>Read More</motion.span>
                    <motion.span
                      className="transition-transform"
                      whileHover={{ x: 4 }}
                    >
                      →
                    </motion.span>
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default BlogListPage;